import {useEffect, useRef, useState, type SyntheticEvent} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useAuth} from '../auth/useAuth'

type Status = 'verifying' | 'needsEmail' | 'error' | 'success'

const VerifyMagicLinkPage = () => {
  const {completeMagicLinkSignIn} = useAuth()
  const navigate = useNavigate()
  const {t} = useTranslation()
  const [status, setStatus] = useState<Status>('verifying')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true
    const run = async () => {
      try {
        await completeMagicLinkSignIn(window.location.href)
        setStatus('success')
        navigate('/projects', {replace: true})
      } catch (err) {
        const message = err instanceof Error ? err.message : ''
        if (message === 'Email required to complete sign-in') {
          setStatus('needsEmail')
        } else {
          setStatus('error')
        }
      }
    }
    void run()
  }, [completeMagicLinkSignIn, navigate])

  const handleSubmitEmail = async (e: SyntheticEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await completeMagicLinkSignIn(window.location.href, email)
      setStatus('success')
      navigate('/projects', {replace: true})
    } catch {
      setStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  if (status === 'verifying' || status === 'success') {
    return (
      <div style={styles.container}>
        <p style={styles.body}>{t('auth.verifying')}</p>
      </div>
    )
  }

  if (status === 'needsEmail') {
    return (
      <div style={styles.container}>
        <h2 style={styles.heading}>{t('auth.logIn')}</h2>
        <p style={styles.body}>{t('auth.enterEmailToContinue')}</p>
        <form onSubmit={handleSubmitEmail} style={styles.form}>
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
            style={styles.input}
          />
          <button type="submit" disabled={submitting || !email} style={styles.primaryButton}>
            {t('auth.confirm')}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>{t('auth.error')}</h2>
      <p style={styles.body}>{t('auth.magicLinkInvalid')}</p>
      <Link to="/login" style={styles.link}>
        {t('auth.backToLogin')}
      </Link>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: 360,
    margin: '48px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '0 20px',
  },
  heading: {
    margin: 0,
    fontSize: 24,
    fontWeight: 600,
  },
  body: {
    margin: 0,
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  input: {
    padding: '10px 12px',
    fontSize: 15,
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.18)',
    background: 'rgba(255, 255, 255, 0.04)',
    color: 'inherit',
    fontFamily: 'inherit',
  },
  primaryButton: {
    padding: '10px 16px',
    fontSize: 15,
    fontWeight: 500,
    borderRadius: 8,
    border: 'none',
    background: 'rgba(255, 255, 255, 0.92)',
    color: '#0a0a0f',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  link: {
    color: 'inherit',
    textDecoration: 'underline',
    fontSize: 14,
  },
}

export default VerifyMagicLinkPage
